import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretLeft, CaretRight, WhatsappLogo, Phone } from '@phosphor-icons/react';
import { BRAND } from '@/theme/brand';
import { supabase } from '@/lib/supabaseClient';
import { normalizeRoutePage } from '@/lib/normalizeRoutePage';
import { resolveRouteImageUrl } from '@/lib/routeImageStorage';
import { useCabRates } from '@/hooks/useCabRates';
import { formatVehicleTierLabel } from '@/lib/vehicleTierLabels';

const truncate = (text, max = 60) => {
  if (!text || text.length <= max) return text;
  return `${text.slice(0, max - 1).trim()}…`;
};

const routeCardTitle = (route) => {
  if (route.pageType === 'route' && route.fromCity && route.toCity) {
    return `${route.fromCity} to ${route.toCity}`;
  }
  if (route.pageType === 'airport') {
    return route.slug === 'hirasar-to-rajkot-taxi' ? 'Rajkot Airport' : 'Ahmedabad Airport';
  }
  return route.h1.replace(/\s*Taxi Service$/i, '').replace(/\s*Rental in Gujarat$/i, ' Rental');
};

const routeImagePath = (route) => resolveRouteImageUrl(route.imageUrl, route.slug);

const formatPriceLine = (tier) => {
  if (tier.amount != null) {
    return `${tier.vehicle}: ₹${tier.amount.toLocaleString('en-IN')}`;
  }
  if (tier.perKm != null) {
    return `${tier.vehicle}: ₹${tier.perKm}/km`;
  }
  return tier.vehicle;
};

const formatCombinedPriceLine = (tiers) =>
  tiers.map((tier) => formatPriceLine(tier)).join(' | ');

const buildRouteWhatsAppMessage = (route) => {
  if (route.toCity) {
    return `Hi, I'd like to book a cab from ${route.fromCity} to ${route.toCity}.`;
  }
  if (route.pageType === 'vehicle') {
    return 'Hi, I\'d like to book a Tempo Traveller in Gujarat.';
  }
  return `Hi, I'd like to book a cab from ${route.fromCity}.`;
};

const buildPriceTiers = (route, fares) => {
  if (route.flatFares?.length >= 2) {
    return route.flatFares.slice(0, 2).map((tier) => ({
      vehicle: formatVehicleTierLabel(tier.vehicle),
      amount: tier.amount,
    }));
  }

  const bookable = fares.filter((f) => f.show_in_booking).slice(0, 2);
  return bookable.map((fare) => ({
    vehicle: formatVehicleTierLabel(fare.name),
    amount:
      route.approxDistanceKm != null
        ? Math.round(route.approxDistanceKm * fare.rate_per_km)
        : null,
    perKm: fare.rate_per_km,
  }));
};

const RouteCardImage = ({ route, title }) => {
  const [failed, setFailed] = useState(false);
  const overlayLabel =
    route.pageType === 'route' && route.toCity
      ? `${route.fromCity} → ${route.toCity}`
      : title;

  if (failed) {
    return (
      <div
        className="relative aspect-video w-full rounded-t-xl bg-gradient-to-br from-[#1B2D5E] to-[#E8651A] flex items-center justify-center px-4"
        aria-hidden
      >
        <p className="text-white font-display font-bold text-lg text-center leading-snug drop-shadow-sm">
          {overlayLabel}
        </p>
      </div>
    );
  }

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-t-xl bg-[#F8F4EE]">
      <img
        src={routeImagePath(route)}
        alt=""
        className="h-full w-full object-cover"
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </div>
  );
};

const RouteCard = ({ route, priceTiers, waPhone }) => {
  const navigate = useNavigate();
  const title = routeCardTitle(route);
  const description = truncate(route.introText);

  const handleCardClick = () => {
    navigate(`/routes/${route.slug}`);
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  return (
    <article
      role="link"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:border-[#E8651A] hover:shadow-md hover:-translate-y-0.5 cursor-pointer font-body"
    >
      <RouteCardImage route={route} title={title} />

      <div className="flex flex-1 flex-col p-4">
        <h3 className="text-base font-bold text-[#1B2D5E] leading-snug">{title}</h3>
        <p className="mt-1.5 text-xs text-gray-500 leading-relaxed min-h-[2.5rem]">{description}</p>

        <p className="mt-3 text-sm font-semibold text-[#E8651A] leading-snug">
          {formatCombinedPriceLine(priceTiers)}
        </p>

        <div className="mt-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
          <a
            href={`https://wa.me/${waPhone}?text=${encodeURIComponent(buildRouteWhatsAppMessage(route))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-bold text-white bg-[#25D366] hover:bg-[#20BD5A] transition-colors"
          >
            <WhatsappLogo size={15} weight="fill" />
            WhatsApp
          </a>
          <a
            href={`tel:${waPhone}`}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-bold text-white bg-[#1B2D5E] hover:bg-[#E8651A] transition-colors"
          >
            <Phone size={15} weight="bold" />
            Call
          </a>
        </div>
      </div>
    </article>
  );
};

const getActiveCardIndex = (container) => {
  if (!container?.children.length) return 0;
  const center = container.scrollLeft + container.clientWidth / 2;
  let best = 0;
  let bestDist = Infinity;
  Array.from(container.children).forEach((child, index) => {
    const childCenter = child.offsetLeft + child.offsetWidth / 2;
    const dist = Math.abs(center - childCenter);
    if (dist < bestDist) {
      bestDist = dist;
      best = index;
    }
  });
  return best;
};

const RoutesCarousel = ({ cards, waPhone }) => {
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setActiveIndex(getActiveCardIndex(el));
        ticking = false;
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();

    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [cards.length]);

  const scrollToIndex = useCallback((index) => {
    const el = scrollRef.current;
    const child = el?.children[index];
    if (!child) return;
    child.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, []);

  const scrollByCard = useCallback((direction) => {
    const next = Math.max(0, Math.min(cards.length - 1, activeIndex + direction));
    scrollToIndex(next);
  }, [activeIndex, cards.length, scrollToIndex]);

  const handleCarouselKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      scrollByCard(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      scrollByCard(1);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => scrollByCard(-1)}
        disabled={activeIndex === 0}
        aria-label="Previous routes"
        className="hidden lg:flex absolute -left-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full border border-[#1B2D5E]/15 bg-white text-[#1B2D5E] shadow-md transition-colors hover:bg-[#1B2D5E] hover:text-white disabled:opacity-30 disabled:pointer-events-none"
      >
        <CaretLeft size={20} weight="bold" />
      </button>

      <button
        type="button"
        onClick={() => scrollByCard(1)}
        disabled={activeIndex === cards.length - 1}
        aria-label="Next routes"
        className="hidden lg:flex absolute -right-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full border border-[#1B2D5E]/15 bg-white text-[#1B2D5E] shadow-md transition-colors hover:bg-[#E8651A] hover:text-white disabled:opacity-30 disabled:pointer-events-none"
      >
        <CaretRight size={20} weight="bold" />
      </button>

      <div
        ref={scrollRef}
        tabIndex={0}
        role="region"
        aria-label="Popular routes carousel"
        aria-live="polite"
        onKeyDown={handleCarouselKeyDown}
        className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 px-1 -mx-1 lg:px-12 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8651A] focus-visible:ring-offset-2 rounded-lg [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {cards.map(({ route, priceTiers }) => (
          <div
            key={route.slug}
            data-route-card
            className="snap-center shrink-0 w-[88vw] max-w-[88vw] md:w-[calc((100%-1.5rem)/2)] md:max-w-[calc((100%-1.5rem)/2)] lg:w-[calc((100%-3rem)/3)] lg:max-w-[calc((100%-3rem)/3)]"
          >
            <RouteCard route={route} priceTiers={priceTiers} waPhone={waPhone} />
          </div>
        ))}
      </div>

      <div className="flex justify-center gap-1.5 mt-6" role="tablist" aria-label="Route carousel pages">
        {cards.map(({ route }, index) => (
          <button
            key={route.slug}
            type="button"
            role="tab"
            aria-selected={index === activeIndex}
            aria-label={`Go to route ${index + 1} of ${cards.length}`}
            onClick={() => scrollToIndex(index)}
            className={`h-2 rounded-full transition-all duration-200 ${
              index === activeIndex ? 'w-6 bg-[#E8651A]' : 'w-2 bg-gray-300 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const PopularRoutesSection = () => {
  const { fares, loading: faresLoading } = useCabRates();
  const waPhone = BRAND.contact.phone;
  const [routes, setRoutes] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { data, error } = await supabase
        .from('route_pages')
        .select('*')
        .eq('is_active', true)
        .order('sort_order')
        .order('title');

      if (cancelled) return;

      if (!error && data?.length) {
        setRoutes(data.map(normalizeRoutePage).filter(Boolean));
      } else {
        setRoutes([]);
      }
      setRoutesLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const cards = useMemo(
    () =>
      routes.map((route) => ({
        route,
        priceTiers: buildPriceTiers(route, fares),
      })),
    [routes, fares],
  );

  const loading = routesLoading || (faresLoading && fares.length === 0);

  return (
    <section className="section-outer bg-white border-t border-gray-100" data-aos="fade-up">
      <div className="section-inner !py-12 !pb-14">
        <div className="text-center mb-8">
          <p className="section-label">POPULAR ROUTES</p>
          <h2 className="section-heading mt-2">Taxi Services Across Gujarat</h2>
          <p className="section-subtext mt-3 max-w-xl mx-auto">
            Book outstation cabs, airport transfers, and group travel with transparent pricing.
          </p>
        </div>

        {loading ? (
          <p className="text-center text-gray-500 font-body text-sm">Loading routes…</p>
        ) : cards.length === 0 ? (
          <p className="text-center text-gray-500 font-body text-sm">No routes available.</p>
        ) : (
          <RoutesCarousel cards={cards} waPhone={waPhone} />
        )}
      </div>
    </section>
  );
};

export default PopularRoutesSection;
